import { useQuery } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { useNavigate } from 'react-router';
import { api } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { AiServiceSummary } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { EmptyState, PageUnavailable, QueryView } from '../../ui/feedback';
import { ActorName, Card, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

/**
 * IDN-06: oversight of personal AI agents, which come from people's browser sign-ins and mirror their owner's
 * access. Service accounts (no owner) have their own page.
 */
export function AiServicesPage() {
  usePageTitle('AI agents');
  useCrumbs([{ label: 'Administration' }, { label: 'AI agents' }]);
  const admin = useIsAdmin();
  const q = useQuery({
    queryKey: ['ai-services'],
    queryFn: () => api.get<AiServiceSummary[]>('/admin/ai-services'),
    enabled: admin,
  });
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <PageHeader
        title="AI agents"
        subtitle="Agents work through the lixionary-jira plugin for Claude Code, Cursor or Antigravity. None of them can approve work, releases or rollbacks. Unattended automation uses a service account instead."
      />
      <QueryView query={q}>
        {(list) => {
          const personal = list.filter((s) => s.owner);
          return (
            <Card
              title="Personal agents"
              subtitle="Created when a person signs in with the lixionary-jira plugin from Claude Code, Cursor or Antigravity. Each one reads and writes exactly its owner's projects."
            >
              {personal.length === 0 ? (
                <EmptyState icon={Bot} title="No personal agents yet">
                  People connect their agent by installing the plugin and signing in. See My AI connections.
                </EmptyState>
              ) : (
                <AgentTable rows={personal} />
              )}
            </Card>
          );
        }}
      </QueryView>
    </div>
  );
}

function AgentTable({ rows }: { rows: AiServiceSummary[] }) {
  const navigate = useNavigate();
  const open = (id: string) => navigate(`/admin/ai-agents/${id}`);
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Agent</th>
            <th>Owner</th>
            <th>State</th>
            <th className="num">Active connections</th>
            <th>Last used</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className="clickable" onClick={() => open(s.id)}>
              <td>
                <a
                  href={`/admin/ai-agents/${s.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    open(s.id);
                  }}
                  className="primary-cell"
                >
                  {s.name}
                </a>
              </td>
              <td className="small">
                <ActorName actor={s.owner} />
              </td>
              <td>{s.active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}</td>
              <td className="num">{s.activeCredentials}</td>
              <td className="small">
                <Timestamp value={s.lastUsedAt} relative />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
